import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import AsyncStorage from "@react-native-async-storage/async-storage";

/**
 * Cached tenant context for feature flag targeting.
 * Stored separately from the full Tenant type to avoid persisting
 * unnecessary data and to keep the store lightweight.
 */
export interface CachedTenantContext {
  tenantId: string;
  tenantName: string;
  realm: string;
  allTenantIds: string[];
  allRealms: string[];
}

interface LastUsedTenantState {
  lastUsedTenantId: string | null;
  /** Cached tenant context for sync access during feature flag init */
  cachedTenantContext: CachedTenantContext | null;
  setLastUsedTenantId: (id: string | null) => void;
  setCachedTenantContext: (context: CachedTenantContext | null) => void;
  clear: () => void;
}

const store = create<LastUsedTenantState>()(
  persist(
    (set) => ({
      lastUsedTenantId: null,
      cachedTenantContext: null,
      setLastUsedTenantId: (id) => set({ lastUsedTenantId: id }),
      setCachedTenantContext: (context) => set({ cachedTenantContext: context }),
      clear: () => set({ lastUsedTenantId: null, cachedTenantContext: null }),
    }),
    {
      name: "last-used-tenant-store",
      storage: createJSONStorage(() => AsyncStorage),
    },
  ),
);

/** React hook to access last used tenant ID */
export function useLastUsedTenantId(): string | null {
  return store((s) => s.lastUsedTenantId);
}

/** Ensure store is hydrated - no-ops if already hydrated */
async function ensureHydrated(): Promise<void> {
  if (!store.persist.hasHydrated()) {
    await store.persist.rehydrate();
  }
}

/** Async getter - waits for hydration from storage */
export async function getLastUsedTenantId(): Promise<string | null> {
  await ensureHydrated();
  return store.getState().lastUsedTenantId;
}

/** Async setter - waits for hydration then sets tenant ID */
export async function setLastUsedTenantId(id: string | null): Promise<void> {
  await ensureHydrated();
  store.getState().setLastUsedTenantId(id);
}

/**
 * Synchronous setter — safe to call after initial app hydration is complete
 * (i.e. from user-initiated actions like tenant switcher clicks).
 * Avoids the async gap that causes the double-click bug (SATD-398).
 */
export function setLastUsedTenantIdSync(id: string | null): void {
  store.getState().setLastUsedTenantId(id);
}

/** Clear last used tenant - call on logout */
export async function clearLastUsedTenant(): Promise<void> {
  await ensureHydrated();
  store.getState().clear();
}

/**
 * Sync getter for cached tenant context - used by feature flags.
 * Returns null if store hasn't hydrated yet (first launch).
 * For subsequent launches, returns the cached context from last session.
 */
export function getCachedTenantContextSync(): CachedTenantContext | null {
  return store.getState().cachedTenantContext;
}

/** Async getter - waits for hydration then returns cached context */
export async function getCachedTenantContext(): Promise<CachedTenantContext | null> {
  await ensureHydrated();
  return store.getState().cachedTenantContext;
}

/** Async setter - waits for hydration then sets cached context */
export async function setCachedTenantContext(context: CachedTenantContext | null): Promise<void> {
  await ensureHydrated();
  store.getState().setCachedTenantContext(context);
}
